import { channelServer, ChannelUnsubscribe, ChannelContractShape } from 'shared/channels';
import type { EntityContract } from 'shared/channels/entity';
import { swSignals } from 'serviceWorker/events/swSignals';
import { runWhenRequestsAllowed } from './runWhenRequestsAllowed';

/** Where the worker reads an entity from and how it syncs it. */
export interface EntitySource<TModel> {
  /** Every item, once the entity is downloaded (starts a sync when it is not). */
  getAll(): Promise<TModel[]>;
  sync(): void;
}

/** An entity source that also reads one item, for details views. */
export interface EntityByIdSource<TModel> extends EntitySource<TModel> {
  /** The item, archived ones included; rejects when there is none. */
  getOneById(id: string): Promise<TModel>;
}

/** Runs an entity sync the page asked for; the permissions are pulled along. */
export function syncWithPermissions(sync: () => void): void {
  swSignals.entitySyncRequested.emit();
  sync();
}

/** Answers the requests of an entity contract from its source. */
export function serveEntityRequests<
  TModel,
  TEvents extends ChannelContractShape['events'],
  TRequests extends ChannelContractShape['requests'],
>(
  contract: EntityContract<TModel, TEvents, TRequests>,
  source: EntitySource<TModel>
): ChannelUnsubscribe {
  return channelServer.handle(contract, {
    getAll: () => source.getAll(),
    sync: () => runWhenRequestsAllowed(() => syncWithPermissions(source.sync)),
  });
}
